import { camelCase } from 'lodash'
import { ReactNode } from 'react'
import styled, { css, StyledComponent } from 'styled-components'

import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import InputArrow from 'shared/icons/InputArrow'
import InputArrowNewFooter from 'shared/icons/InputArrowNewFooter'
import { labelStyles, TextInput, TextInputProps } from 'shared/inputs/TextInput'
import { colors as fc2Colors } from 'shared/lib'
import LoaderSVG from 'shared/svg/loader.svg'
import SuccessCheckSVG from 'shared/svg/success_check.svg'
import { H6 } from 'shared/text'
import { colors } from 'shared/theme'
import { t } from 'localization'

const TP = 'shared.inputs.ComboBox'

export interface ComboBoxProps extends TextInputProps {
  description?: string | ReactNode
  icon?: StyledComponent<any, { className?: string }>
  isLoading?: boolean
  successMessage?: string
  title?: string
}

const ComboBox = ({
  className,
  dataQa,
  description,
  icon: IconFromProps,
  isLoading = false,
  label,
  name,
  onChange,
  successMessage,
  title,
  type = 'text',
}: ComboBoxProps) => {
  const baseDataQa = dataQa || `${camelCase(label)}ComboBox`
  const getComboBoxDataQa = (postFix = '') => `${baseDataQa}${postFix}`
  const isNewFooter = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_ENABLE_NEW_FOOTER)

  return (
    <Container
      role="form"
      className={className}
      data-qa-loading={isLoading}
      data-qa={getComboBoxDataQa()}
    >
      {title && (
        <Title $isNewFooter={isNewFooter} data-qa={getComboBoxDataQa('Title')}>
          {title}
        </Title>
      )}
      <InputWrapper $isNewFooter={isNewFooter}>
        <ComboInput
          $isNewFooter={isNewFooter}
          dataQa={getComboBoxDataQa('TextInput')}
          label={label}
          name={name}
          onChange={onChange}
          type={type}
        />
        <ComboSubmit
          $isNewFooter={isNewFooter}
          aria-label={t(`${TP}.submit`, 'Submit')}
          data-qa={getComboBoxDataQa('SubmitButton')}
          disabled={isLoading}
          type="submit"
        >
          {successMessage ? (
            <SuccessCheck />
          ) : isLoading ? (
            <Loader />
          ) : IconFromProps ? (
            <IconFromProps />
          ) : isNewFooter ? (
            <NewFooterArrow />
          ) : (
            <Arrow />
          )}
        </ComboSubmit>
      </InputWrapper>
      {successMessage && (
        <SuccessMessage data-qa={getComboBoxDataQa('SuccessMessage')}>
          {successMessage}
        </SuccessMessage>
      )}
      {description && (
        <Description $isNewFooter={isNewFooter} data-qa={getComboBoxDataQa('Description')}>
          {description}
        </Description>
      )}
    </Container>
  )
}

const Container = styled.div`
  position: relative;
`

/**
 * The new footer's signup is a single bordered box with the label above it and a
 * chromeless submit inside; the legacy one stitches a bordered input to a
 * bordered button. Both are kept until the flag is removed.
 */
const Title = styled(H6)<{ $isNewFooter: boolean }>`
  ${({ $isNewFooter }) =>
    $isNewFooter
      ? css`
          color: ${colors.white};
          font-size: 12px;
          font-weight: 400;
          line-height: 1.25;
          margin: 0 0 24px;
          text-transform: uppercase;
        `
      : css`
          margin-bottom: 25px;
        `}
`

const InputWrapper = styled.div<{ $isNewFooter: boolean }>`
  display: flex;
  position: relative;

  ${({ $isNewFooter }) =>
    $isNewFooter
      ? css`
          align-items: center;
          border: 1px solid ${colors.white};
          border-radius: 4px;
          height: 50px;
          padding: 8px 8px 8px 16px;
          transition: border-color 250ms;

          /* stylelint-disable-next-line selector-class-pattern */
          &:has(.hasError) {
            border-color: ${colors.red};
          }
        `
      : css`
          align-items: flex-start;
        `}
`

/* stylelint-disable */
const ComboInput = styled(TextInput)<{ $isNewFooter: boolean }>`
  flex: 1;
  margin-top: 0;

  ${({ $isNewFooter }) =>
    $isNewFooter
      ? css`
          /*
           * The title labels the field visually, so the label is hidden — but
           * kept in the accessibility tree rather than removed, so the input
           * still has an accessible name.
           */
          label {
            clip: rect(0, 0, 0, 0);
            height: 1px;
            overflow: hidden;
            position: absolute;
            white-space: nowrap;
            width: 1px;
          }

          input {
            background: none;
            border: none;
            font-size: 14px;
            height: auto;
            padding: 0;
          }

          /* Validation messages sit below the box instead of displacing it */
          & > div {
            font-size: 12px;
            left: 0;
            margin-top: 6px;
            position: absolute;
            right: 0;
            top: 100%;
          }
        `
      : css`
          input {
            border-right: 0;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
          }
          ${InputWrapper}:focus-within & input {
            border-color: ${colors.white};
          }
          ${InputWrapper} &.hasError input {
            border-color: ${colors.red};
          }
        `}
`
/* stylelint-enable */

const SuccessMessage = styled.div`
  ${labelStyles}
  color: ${colors.white};
  margin-top: 10px;
`

const Arrow = styled(InputArrow)`
  transition: transform 125ms ease-out;
  transform: translate3d(0, 0, 0);
`
const NewFooterArrow = styled(InputArrowNewFooter)`
  transition: transform 125ms ease-out;
  transform: translate3d(0, 0, 0);
`
const Loader = styled(LoaderSVG)`
  transform: translate3d(1px, -3px, 0);
`
const SuccessCheck = styled(SuccessCheckSVG)`
  transform: translate3d(0, 3px, 0);
`

/* stylelint-disable */
const ComboSubmit = styled.button<{ $isNewFooter: boolean }>`
  ${({ $isNewFooter }) =>
    $isNewFooter
      ? /* Sits inside the wrapper's border, so it carries no chrome of its own */
        css`
          background: none;
          border: none;
          height: 100%;
          margin: 0;
          min-width: 24px;
          padding: 4px 8px;
        `
      : css`
          background-color: ${colors.offBlack};
          border: 1px solid ${colors.lighterGray};
          border-bottom-right-radius: 2px;
          border-top-right-radius: 2px;
          border-left: 0;
          height: 50px;
          margin-left: 0;
          margin-top: 28px;
          min-width: 50px;
          padding: 0 20px;
          transition: border-color 250ms;
          ${InputWrapper}:focus-within & {
            border-color: ${colors.white};
          }
          ${InputWrapper} .hasError + & {
            border-color: ${colors.red};
          }
        `}

  &:focus > ${Arrow},
  &:hover > ${Arrow},
  &:focus > ${NewFooterArrow},
  &:hover > ${NewFooterArrow} {
    transform: translate3d(18%, 0, 0);
  }
`
/* stylelint-enable */

// this is a bad code smell
// anchors should be globally styled
// FIX ME
const Description = styled.p<{ $isNewFooter: boolean }>`
  ${({ $isNewFooter }) =>
    $isNewFooter
      ? css`
          color: ${fc2Colors.FC2_GREY};
          font-size: 12px;
          line-height: 1.5;
          margin: 24px 0 0;
        `
      : css`
          color: ${colors.mediumGray};
          font-size: 14px;
          line-height: 1.3;
        `}

  &&& a {
    color: ${({ $isNewFooter }) => ($isNewFooter ? fc2Colors.FC2_GREY : colors.mediumGray)};
    text-decoration: underline;
  }

  &&& a:hover {
    color: ${colors.lighterGray};
    text-decoration: underline;
  }
`

export default ComboBox
